import { useLocation } from '@fs/zion-router'
import React, { createContext, useEffect, useMemo, useState } from 'react'

export const LookupContext = createContext()

export const ARTIFACT_CONTEXT = 'artifact'
export const CHAT_CONTEXT = 'chat'
export const CIS_USER_ID_CONTEXT = 'cisUserId'
export const CMIS_ID_CONTEXT = 'cmisId'
export const CONTACT_NAME_CONTEXT = 'contactName'
export const CTR_ID_CONTEXT = 'ctrId'
export const DISCUSSION_ID_CONTEXT = 'discussionId'
export const GROUP_ID_CONTEXT = 'groupId'
export const MEMBERSHIP_UUID_CONTEXT = 'uuid'
export const MESSAGE_ID_CONTEXT = 'messageId'
export const MRN_ID_CONTEXT = 'mrn'
export const OWS_OSID_CONTEXT = 'oOsid'
export const OWS_ID_CONTEXT = 'owsId'
export const PERSON_ID_CONTEXT = 'personId'
export const PIN_ID_CONTEXT = 'pinId'
export const RAN_ID_CONTEXT = 'ranId'
export const COUPLE_RELATIONSHIP_ID_CONTEXT = 'coupleRelationshipId'
export const PARENT_CHILD_RELATIONSHIP_ID_CONTEXT = 'parentRelationshipId'
export const ASSOCIATION_RELATIONSHIP_ID_CONTEXT = 'associateRelationshipId'
export const REQUEST_ID_CONTEXT = 'requestId'
export const REQUEST_OSID_CONTEXT = 'rOsid'
export const TEMPLE_CODE_CONTEXT = 'templeCode'
export const TEMPLE_SAFE_CONTEXT = 'templeSafe'
export const TREE_ID_CONTEXT = 'treeId'
export const USER_ID_CONTEXT = 'userId'
export const USERNAME_CONTEXT = 'username'
export const UNIT_NUMBER_CONTEXT = 'unitNumber'

const getContextFromPath = ({ path, search }) => {
  if (path) {
    const pathSegments = path.split('/', 3).filter((segment) => segment && segment !== '')
    if (pathSegments.length > 0) {
      const firstPathSegment = pathSegments[0]
      if (firstPathSegment === 'temple') {
        return getTempleContext(pathSegments)
      }
      switch (firstPathSegment) {
        case 'artifacts':
          return ARTIFACT_CONTEXT
        case 'chat':
          return CHAT_CONTEXT
        case 'group':
          return GROUP_ID_CONTEXT
        case 'person': {
          const ranIdItem = search
            .substring(1)
            .split('&')
            .find((item) => item.split('=')[0] === 'ranId')
          if (ranIdItem) {
            return RAN_ID_CONTEXT
          }
          return PERSON_ID_CONTEXT
        }
        case 'reservation':
          return RAN_ID_CONTEXT
        case 'tree':
          return TREE_ID_CONTEXT
        case 'user': {
          const contextItem = search
            .substring(1)
            .split('&')
            .find((item) => item.split('=')[0] === 'context')
          if (contextItem) {
            return contextItem.split('=')[1]
          }
          return USER_ID_CONTEXT
        }
        case 'unit':
          return UNIT_NUMBER_CONTEXT
        case 'relationship': {
          const secondPathSegment = pathSegments[1]
          if (secondPathSegment === 'parentchild') return PARENT_CHILD_RELATIONSHIP_ID_CONTEXT
          if (secondPathSegment === 'association') return ASSOCIATION_RELATIONSHIP_ID_CONTEXT
          return COUPLE_RELATIONSHIP_ID_CONTEXT
        }
        default: {
          if (firstPathSegment !== 'person') {
            return PERSON_ID_CONTEXT
          }
        }
      }
    }
  }
  return PERSON_ID_CONTEXT

  function getTempleContext(pathSegments) {
    if (pathSegments.length > 1) {
      switch (pathSegments[1]) {
        case 'ctr':
          return CTR_ID_CONTEXT
        case 'ows':
          if (search.includes('osid')) return OWS_OSID_CONTEXT
          return OWS_ID_CONTEXT
        case 'request':
          if (search.includes('osid')) return REQUEST_OSID_CONTEXT
          return REQUEST_ID_CONTEXT
        case 'inventory-info':
          return PIN_ID_CONTEXT
        default:
          return TEMPLE_CODE_CONTEXT
      }
    }
    return TEMPLE_CODE_CONTEXT
  }
}

const getValueFromPath = ({ path, context, search }) => {
  let value = null
  if (path) {
    switch (context) {
      case PERSON_ID_CONTEXT: {
        const personPathParts = path.split('/')
        if (personPathParts[1] === 'person') {
          value = path.split('/')[2]
          if (!value) {
            value = ''
          }
        } else {
          value = ''
        }
        break
      }
      case CHAT_CONTEXT:
      case CIS_USER_ID_CONTEXT:
      case CMIS_ID_CONTEXT:
      case GROUP_ID_CONTEXT:
      case MEMBERSHIP_UUID_CONTEXT:
      case MRN_ID_CONTEXT:
      case TREE_ID_CONTEXT:
      case UNIT_NUMBER_CONTEXT:
      case USER_ID_CONTEXT: {
        value = path.split('/')[2]
        if (!value) {
          value = ''
        }
        break
      }
      case ASSOCIATION_RELATIONSHIP_ID_CONTEXT:
      case COUPLE_RELATIONSHIP_ID_CONTEXT:
      case CTR_ID_CONTEXT:
      case OWS_ID_CONTEXT:
      case OWS_OSID_CONTEXT:
      case PARENT_CHILD_RELATIONSHIP_ID_CONTEXT:
      case PIN_ID_CONTEXT:
      case REQUEST_ID_CONTEXT: {
        value = path.split('/')[3]
        if (!value) {
          value = ''
        }
        break
      }
      case ARTIFACT_CONTEXT: {
        const artifactPathParts = path.split('/')
        if (artifactPathParts[2] !== 'artifact') {
          value = ''
        } else {
          value = artifactPathParts[3]
        }
        break
      }
      case CONTACT_NAME_CONTEXT:
      case USERNAME_CONTEXT: {
        value = path.split('/')[2]
        if (value.indexOf('.') !== -1) {
          value = `"${value}"`
        }
        if (!value) {
          value = ''
        }
        break
      }
      case TEMPLE_CODE_CONTEXT:
        value =
          search
            .substring(1)
            .split('&')
            .find((item) => item.split('=')[0] === 'templeCode')
            ?.split('=')[1] || ''
        break
      case RAN_ID_CONTEXT: {
        value = search
          .substring(1)
          .split('&')
          .find((item) => item.split('=')[0] === 'ranId')
          .split('=')[1]
        break
      }
      default:
        break
    }
  }
  return value
}

function LookupContextProvider({ children }) {
  const location = useLocation()
  const [lookupValue, setLookupValue] = useState()
  const [lookupContext, setLookupContext] = useState(() => {
    return getContextFromPath({
      path: location ? location.pathname : PERSON_ID_CONTEXT,
      search: location.search,
    })
  })

  useEffect(() => {
    const context = getContextFromPath({
      path: location ? location.pathname : PERSON_ID_CONTEXT,
      search: location.search,
    })
    setLookupContext(context)
    const value = getValueFromPath({
      path: location ? location.pathname : PERSON_ID_CONTEXT,
      context,
      search: location.search,
    })
    if (value || value === '') {
      setLookupValue(value)
    }
  }, [location])

  const value = useMemo(
    () => ({
      lookupContext,
      setLookupContext,
      lookupValue,
    }),
    [lookupContext, setLookupContext, lookupValue]
  )
  return <LookupContext.Provider value={value}>{children}</LookupContext.Provider>
}

export default LookupContextProvider
